// src/core/config-fetcher.ts

import type { RemoteConfig } from '../types'
import { CONFIG_CACHE_TTL } from '../constants/defaults'
import { saveConfigCache, loadConfigCache } from './storage'
import { debugLog } from '../utils/helpers'

/**
 * Fetch remote config from Payload API
 */
export async function fetchRemoteConfig(apiUrl: string): Promise<RemoteConfig | null> {
  try {
    debugLog('Fetching remote config from', apiUrl)

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      cache: 'no-cache',
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const data = await response.json()
    debugLog('Remote config fetched', data)

    // Save to cache
    saveConfigCache(data, CONFIG_CACHE_TTL)

    return data
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[MEUI Cookies] Failed to fetch remote config:', error)
    return null
  }
}

/**
 * Get remote config with cache fallback
 */
export async function getRemoteConfig(apiUrl: string): Promise<RemoteConfig | null> {
  // Try cache first
  const cached = loadConfigCache(CONFIG_CACHE_TTL)
  if (cached) {
    debugLog('Using cached config')

    // Fetch new config in background (don't await)
    fetchRemoteConfig(apiUrl).catch(() => {
      // Silent fail - cache is already good
    })

    return cached
  }

  // Fetch fresh config
  return await fetchRemoteConfig(apiUrl)
}

/**
 * Preload remote config (background fetch)
 */
export function preloadRemoteConfig(apiUrl: string): void {
  // Check if cache is valid
  const cached = loadConfigCache(CONFIG_CACHE_TTL)
  if (cached) {
    debugLog('Config already cached, skipping preload')
    return
  }

  // Fetch in background
  fetchRemoteConfig(apiUrl)
    .then(() => {
      debugLog('Config preloaded successfully')
    })
    .catch(() => {
      // Silent fail - will use fallback
    })
}
